// Static text content for forms — policies, risks, consent language

export const SALON_POLICIES = {
  cancellation: {
    title: 'CANCELLATION POLICY',
    text: 'We have reserved this spot just for you! If you are unable to make your appointment or need to reschedule, please do so with at least 12 hours\u2019 notice.'
  },
  arrival: {
    title: 'ARRIVAL TIME',
    text: 'An early arrival allows for a relaxed and unhurried experience. Please arrive no less than 5 minutes early for your scheduled appointment. Plan to arrive early in case of traffic or parking issues.'
  },
  lateArrivals: {
    title: 'LATE ARRIVALS',
    text: 'If you arrive late, we will do our best to accommodate you as time permits. However, we are often booked back-to-back and if another guest is booked after you, the service must end on time. If you arrive more than 5 mins late, we may need to perform a modified service or reschedule you in the next available time slot.'
  },
  noShow: {
    title: 'NO SHOW APPOINTMENTS',
    text: 'When you miss an appointment with us, we not only lose your business, but also the potential business of other clients who could have scheduled an appointment for the same time. No-show appointments will be charged 100% of the cost of services booked.'
  },
  phoneUse: {
    title: 'MOBILE PHONE USE & RECORDINGS',
    text: 'To preserve the tranquil setting in our salons, we respectfully ask that you turn your cellphone on silent or vibrate mode. Please keep personal phone conversations to a minimum. In the common spaces of our salon, we ask you to kindly refrain from loud conversations, using speaker phone or watching videos with the sound on. Our team is a little camera shy. The use of recording devices or apps (on your cellphone or otherwise) are strictly prohibited in our service rooms.'
  }
};

export const RISKS_AND_CONTRAINDICATIONS = {
  waxing: {
    emoji: '\uD83E\uDDF4',
    title: 'WAXING',
    contraindications: [
      { text: 'Use of ', bold: 'Accutane (within 12 months)' },
      { text: 'Use of ', bold: 'topical/oral retinoids', after: ' (e.g., Retin-A, Differin)' },
      { text: 'Recent ', bold: 'chemical peels or microdermabrasion', after: ' (last 2\u20134 weeks)' },
      { text: 'Use of ', bold: 'AHA/BHA, glycolic, salicylic acids' },
      { text: 'Active ', bold: 'acne, sunburn, rash, or open wounds' },
      { text: 'Recent ', bold: 'cosmetic injectables', after: ' (Botox/fillers) in treatment area' },
      { text: 'Skin-thinning medications' },
      { text: 'History of ', bold: 'herpes simplex', after: ' (cold sores) in the area (e.g., lip waxing)' }
    ],
    risks: [
      'Skin lifting or removal',
      'Redness, irritation, tenderness',
      'Ingrown hairs',
      'Breakouts or bumps',
      'Bruising or bleeding',
      'Hyperpigmentation or scarring',
      'Burns or folliculitis',
      'Reactivation of cold sores'
    ]
  },
  lashLift: {
    emoji: '\uD83C\uDF3F',
    title: 'LASH LIFT',
    contraindications: [
      { text: 'Eye infections (conjunctivitis, styes)' },
      { text: 'Recent eye surgery or LASIK' },
      { text: 'Very sensitive eyes or allergies to perming solutions' },
      { text: 'Pregnancy (precautionary \u2014 sensitivity may be heightened)' },
      { text: 'Use of lash serums (can increase sensitivity or lift unpredictably)' },
      { text: 'Chemotherapy or medical treatments affecting hair growth' }
    ],
    risks: [
      'Eye irritation or allergic reaction',
      'Dryness or redness',
      'Over-processing or lash damage',
      'Uneven lift results'
    ]
  },
  tinting: {
    emoji: '\uD83D\uDD8C\uFE0F',
    title: 'TINTING (Brow or Lash)',
    contraindications: [
      { text: 'Eye infections or irritation' },
      { text: 'History of allergic reactions to dye or tint products' },
      { text: 'Very sensitive skin or eyes' },
      { text: 'Recent microblading (for brows)' },
      { text: 'Pregnancy (precautionary)' }
    ],
    risks: [
      'Allergic reaction to dye',
      'Eye irritation (lash tinting)',
      'Skin staining',
      'Uneven color or patchiness'
    ]
  },
  sprayTan: {
    emoji: '\u2600\uFE0F',
    title: 'SPRAY TAN',
    contraindications: [
      { text: 'Open cuts, wounds, or rashes' },
      { text: 'Active skin infections (fungal, bacterial)' },
      { text: 'Known allergy to DHA (active tanning ingredient)' },
      { text: 'Severe respiratory issues (inhaling mist may trigger asthma)' },
      { text: 'Pregnancy (generally safe, but patch test is recommended)' }
    ],
    risks: [
      'Allergic reaction or rash',
      'Uneven colour or streaking',
      'Staining of nails/hair if not properly prepped',
      'Temporary bronzer transfer to clothing or sheets',
      'Inhaling mist (rare, but avoidable with nose filters)'
    ]
  },
  nails: {
    emoji: '\uD83D\uDC85',
    title: 'NAILS',
    contraindications: [
      { text: 'Nail or skin infections (fungal, bacterial)' },
      { text: 'Open cuts or wounds near nails' },
      { text: 'Extremely thin or damaged nails' },
      { text: 'Known allergy to nail products (acrylate sensitivity)' },
      { text: 'Poor circulation (especially for diabetic clients \u2014 use caution)' }
    ],
    risks: [
      'Nail damage from over-filing or improper removal',
      'Lifting or separation of product',
      'Allergic reaction to nail products',
      'Heat spikes during curing (gel/acrylic)',
      'Risk of infection if skin is broken',
      'Overcutting of cuticles or surrounding tissue'
    ]
  }
};

// Map from service checkbox keys to risks keys
export const SERVICE_TO_RISKS_MAP = {
  waxing: 'waxing',
  tinting: 'tinting',
  lashLift: 'lashLift',
  browLamination: null, // has its own separate form
  nails: 'nails',
  sprayTan: 'sprayTan',
  laserHairRemoval: null  // has its own separate form
};

export const GUEST_INTAKE_CONSENT = [
  'I certify that the information I have provided in this form is true, complete, and accurate to the best of my knowledge. I understand that by receiving services at Cutie Pie Wax Bar, I may be exposed to certain risks associated with cosmetic and personal care treatments.',
  'I confirm that I have read and understood the information provided regarding the risks, side effects, and contraindications of the services I am receiving today. I acknowledge that I have had the opportunity to ask questions and that all my questions have been answered to my satisfaction.',
  'I hereby give my voluntary consent to receive services from Cutie Pie Wax Bar Inc. I release and hold harmless Cutie Pie Wax Bar Inc., its employees, directors, independent contractors, and service providers from any liability, claims, or damages that may result from the services received, except in cases of gross negligence.',
  'I understand that this Consent and Release constitutes full disclosure and supersedes any previous written or verbal disclosures. This agreement shall remain valid for all future appointments and services unless revoked in writing.',
  'It is my responsibility to inform my esthetician or technician of any changes to my medical history, skincare routine, or health conditions that may affect the safety or outcome of my treatments.',
  'By signing below, I confirm that I have read and understood the above, accept the risks, and agree to the terms and policies outlined.'
];

export const BROW_LAM_INTAKE_CONSENT = 'By signing below, I agree to the following: I have completed this form to the best of my ability and knowledge. I agree to inform the technician of any changes in the above information. I agree that I do not have any condition(s) that would make the requested treatment unsuitable. I will inform the technician of any discomfort I may experience at any time during my treatment to allow them to adjust accordingly. I agree to waive all liability toward my technician and the salon for any injury or damages incurred due to any misrepresentation of my health.';

export const BROW_LAM_CONSENT_INTRO = 'Although every precaution will be taken to ensure my safety and wellbeing before, during and after the brow lamination process, I am aware of the following information and possible risks. Please initial:';

export const BROW_LAM_CONSENT_STATEMENTS = [
  'I understand that during the treatment, despite all precautionary measures, injury is possible I will not hold the technician or business performing this service on me responsible in any way for any damages or issues that may arise as a result of having the brow lamination procedure performed on me.',
  'I understand that some irritation, itching or burning may occur to the skin which comes in contact with the lamination agent.',
  'I understand that an allergic reaction is possible.',
  'I understand that it is imperative that I disclose all of the information requested on the Client Intake Form.',
  'I have cited all conditions and circumstances regarding my health history, medications being taken, and any past reactions to products or medications.',
  'I consent to \u201Cbefore and after\u201D photographs for the purpose of documentation, potential advertising and promotional purposes.',
  'I agree that if I experience any ill effects with brows that I will contact the technician that performed this procedure.',
  'I understand that brow lamination is the process of restructuring the brow hairs to keep them in a desired shape, but it is my responsibility to brush my brows daily to maintain the desired look.',
  'I understand that I need to keep my eyebrows dry for 24 hours after the brow lamination process',
  'I understand that brow lamination is not recommended for people with following, and I hereby certify that none of the below apply to me:'
];

export const BROW_LAM_CONTRAINDICATION_LIST = [
  'Alopecia',
  'Conjunctivitis',
  'Currently taking blood thinners, brow growth serum, retinol, Accutane, or AHAs or BHAs',
  'Eczema',
  'Pregnant/breastfeeding',
  'Psoriasis',
  'Recent eye surgery',
  'Recent microblading',
  'Retinol',
  'Sensitive skin',
  'Scar tissue in treatment area',
  'Sunburn'
];

export const BROW_LAM_FINAL_CONSENT = 'This agreement will remain in effect for this procedure and all future follow-ups conducted by the technician. I understand that this consent agreement is legal and binding. I have read and fully understand all information in this agreement. I am over 18 years of age and consent to the agreement and to brow lamination procedure.';
